// src/pages/CategoryPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
} from 'firebase/firestore';
import { db } from '../firebase';
import BookCard from '../components/BookCard';
import { getResponsivePageSize } from '../utils/pagination';
import { isReservationActive, listReservationsByBookIds } from '../utils/reservations';
import { loadBookTemplatesByIds, primeBookTemplatesCache } from '../utils/templateCache';

const CATEGORY_LABEL = {
  fiction: 'Історії, що захоплюють',
  nonfiction: 'Дізнатись нове',
  'non-fiction': 'Дізнатись нове',
  kids: 'Для дітей',
};

const normalizeCategoryName = (raw) => {
  const v = (raw || '').toString().trim().toLowerCase();
  if (v === 'non-fiction') return 'nonfiction';
  return v || 'recent';
};

const categoryAliases = (name) => {
  if (name === 'nonfiction') return ['nonfiction', 'non-fiction'];
  return [name];
};

const chunk = (arr, size = 10) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export default function CategoryPage() {
  const pageSize = getResponsivePageSize();
  const [params] = useSearchParams();
  const rawName = params.get('name') || 'recent';
  const name = normalizeCategoryName(rawName);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [items, setItems] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [sortMode, setSortMode] = useState('createdAt');
  const [activeCategory, setActiveCategory] = useState(null);

  const title = useMemo(
    () => (name === 'recent' ? 'Останні додані' : (CATEGORY_LABEL[name] || 'Книги')),
    [name]
  );

  const buildCategoryQuery = (after, mode = sortMode, categoryName = activeCategory || name) => {
    const col = collection(db, 'bookTemplates');
    let q = mode === 'createdAt'
      ? query(col, where('category', '==', categoryName), orderBy('createdAt', 'desc'), limit(pageSize))
      : query(col, where('category', '==', categoryName), orderBy('borrowCount', 'desc'), limit(pageSize));
    if (after) q = query(q, startAfter(after));
    return q;
  };

  const filterTemplatesByFreeCopies = async (templates) => {
    if (!Array.isArray(templates) || templates.length === 0) return [];

    const tplIds = templates.map((t) => t.id).filter(Boolean);
    const books = [];
    for (const pack of chunk(tplIds, 10)) {
      const s = await getDocs(
        query(
          collection(db, 'books'),
          where('status', '==', 'available'),
          where('templateId', 'in', pack),
        )
      );
      books.push(...s.docs.map((d) => ({ id: d.id, ...d.data() })));
    }
    const reservations = await listReservationsByBookIds(books.map((b) => b.id));
    const reservedBookIds = new Set(
      reservations.filter((r) => isReservationActive(r)).map((r) => String(r.bookId || r.id))
    );

    const freeByTpl = new Map();
    books.forEach((b) => {
      if (reservedBookIds.has(String(b.id))) return;
      const t = String(b.templateId || '');
      if (!t) return;
      freeByTpl.set(t, (freeByTpl.get(t) || 0) + 1);
    });

    return templates.filter((t) => (freeByTpl.get(String(t.id)) || 0) > 0);
  };

  const fetchRecentPage = async (after = null) => {
    const rawLimit = pageSize * 3;
    let snap;

    try {
      let q = query(collection(db, 'books'), orderBy('createdAtTs', 'desc'), limit(rawLimit));
      if (after) q = query(collection(db, 'books'), orderBy('createdAtTs', 'desc'), startAfter(after), limit(rawLimit));
      snap = await getDocs(q);
    } catch {
      // Fallback for legacy data without createdAtTs/index.
      if (after) return { rows: [], nextLastDoc: null, more: false };
      snap = await getDocs(query(collection(db, 'books'), limit(rawLimit)));
    }

    const bookRows = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((b) => b.status === 'available' && b.templateId);

    const templateIds = Array.from(new Set(bookRows.map((b) => b.templateId).filter(Boolean)));
    const templates = await loadBookTemplatesByIds(templateIds);
    const tplMap = new Map(templates.map((t) => [t.id, t]));

    const dedup = new Set();
    const rows = [];
    for (const b of bookRows) {
      const t = tplMap.get(b.templateId);
      if (!t) continue;
      const key = t.isbn || t.id;
      if (dedup.has(key)) continue;
      dedup.add(key);
      rows.push({
        id: t.id || b.id,
        isbn: b.isbn || t.isbn || '',
        title: t.title || b.title || '',
        author: t.author || b.author || '',
        coverUrl: t.coverUrl || b.coverUrl || '',
        borrowPrice: t.borrowPrice ?? t.priceBorrow ?? t.price ?? b.borrowPrice ?? b.priceBorrow ?? b.price ?? null,
        borrowCount: t.borrowCount ?? b.borrowCount ?? 0,
      });
      if (rows.length === pageSize) break;
    }

    return {
      rows,
      nextLastDoc: snap.docs[snap.docs.length - 1] || null,
      more: snap.size === rawLimit,
    };
  };

  const loadFirst = async () => {
    setLoading(true);
    setItems([]);
    setLastDoc(null);
    setHasMore(true);

    if (name === 'recent') {
      const first = await fetchRecentPage(null);
      setItems(first.rows);
      setLastDoc(first.nextLastDoc);
      setHasMore(first.more && first.nextLastDoc != null);
      setSortMode('createdAt');
      setActiveCategory(null);
      setLoading(false);
      return;
    }

    let snap = null;
    let chosenMode = 'createdAt';
    let chosenCategory = name;
    const aliases = categoryAliases(name);

    for (const alias of aliases) {
      try {
        const test = await getDocs(buildCategoryQuery(null, 'createdAt', alias));
        if (test.size > 0) {
          snap = test;
          chosenCategory = alias;
          chosenMode = 'createdAt';
          break;
        }
        if (!snap) {
          snap = test;
          chosenCategory = alias;
        }
      } catch {
        // Fallback to borrowCount.
      }
    }

    if (!snap || snap.size === 0) {
      for (const alias of aliases) {
        try {
          const test = await getDocs(buildCategoryQuery(null, 'borrowCount', alias));
          if (test.size > 0) {
            snap = test;
            chosenCategory = alias;
            chosenMode = 'borrowCount';
            break;
          }
          if (!snap) {
            snap = test;
            chosenCategory = alias;
            chosenMode = 'borrowCount';
          }
        } catch {
          // Keep searching aliases.
        }
      }
    }

    setSortMode(chosenMode);
    setActiveCategory(chosenCategory);

    if (!snap) snap = { docs: [], size: 0 };
    const rawTemplates = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((b) => (b.availableCount ?? 0) > 0);
    primeBookTemplatesCache(rawTemplates);
    const freeTemplates = await filterTemplatesByFreeCopies(rawTemplates);
    setItems(freeTemplates);
    setLastDoc(snap.docs[snap.docs.length - 1] || null);
    setHasMore(snap.size === pageSize);
    setLoading(false);
  };

  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);

    if (name === 'recent') {
      const next = await fetchRecentPage(lastDoc);
      setItems((prev) => [...prev, ...next.rows]);
      setLastDoc(next.nextLastDoc);
      setHasMore(next.more && next.nextLastDoc != null);
      setLoadingMore(false);
      return;
    }

    const snap = await getDocs(buildCategoryQuery(lastDoc, sortMode, activeCategory || name));
    const rawTemplates = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((b) => (b.availableCount ?? 0) > 0);
    primeBookTemplatesCache(rawTemplates);
    const freeTemplates = await filterTemplatesByFreeCopies(rawTemplates);
    setItems((prev) => [...prev, ...freeTemplates]);
    setLastDoc(snap.docs[snap.docs.length - 1] || null);
    setHasMore(snap.size === pageSize);
    setLoadingMore(false);
  };

  useEffect(() => { loadFirst(); }, [name]);
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [name]);

  const [winW, setWinW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const onResize = () => setWinW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const cols =
    winW <= 540 ? 'repeat(2, minmax(0,1fr))' :
    winW <= 900 ? 'repeat(3, minmax(0,1fr))' :
                  'repeat(4, minmax(0,1fr))';

  return (
    <div style={{ background: '#F6F4EF', minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 16 }}>
        <h1 style={{ margin: '8px 0 16px' }}>{title}</h1>

        {loading ? <div>Завантаження...</div> : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 20 }}>
              {items.map((b) => <BookCard key={`${b.id}-${b.isbn || ''}`} book={b} />)}
            </div>

            {!items.length && <div style={{ color: '#6b7280' }}>Книг поки немає.</div>}

            <div style={{ textAlign: 'center', margin: '20px 0' }}>
              {hasMore ? (
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #0b5226', background: '#0b5226', color: '#fff', fontWeight: 700 }}
                >
                  {loadingMore ? 'Завантаження...' : 'Показати ще'}
                </button>
              ) : (items.length > 0 && <div style={{ color: '#6b7280' }}>Усе показано</div>)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}



